import { useQuery } from '@apollo/client';
import type { MouseEvent, FC } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import {
  EntityPageFocusType,
  EntityPagePublicationStatus,
} from '@xing-com/crate-common-graphql-types';
import {
  COMPANY_PROFILE_CREATION_URL,
  TEAMPERMISSIONS_URL,
  relocate,
} from '@xing-com/crate-companies-shared';
import { IconArrowRight } from '@xing-com/icons';

import { SectionBrandmanagerDocument } from '../../graphql/queries/section-brandmanager.gql-types';
import { useNwtTrackingCompanies } from '../../utils/tracking';
import { SectionGridContainer } from '../section-container/section-container.styled';

import * as Styled from './section-brandmanager.styles';

enum BrandmanagerStates {
  CREATE = 'CREATE',
  FINALIZE = 'FINALIZE',
  MANAGE = 'MANAGE',
}
const translationsStates = {
  [BrandmanagerStates.CREATE]: {
    box: 'CP_OVERVIEW_BRANDMANAGER_SECTION_BOX.CREATE',
    button: 'CP_OVERVIEW_BRANDMANAGER_SECTION_BUTTON.CREATE',
  },
  [BrandmanagerStates.FINALIZE]: {
    box: 'CP_OVERVIEW_BRANDMANAGER_SECTION_BOX.FINALIZE',
    button: 'CP_OVERVIEW_BRANDMANAGER_SECTION_BUTTON.FINALIZE',
  },
  [BrandmanagerStates.MANAGE]: {
    box: 'CP_OVERVIEW_BRANDMANAGER_SECTION_BOX.MANAGE',
    button: 'CP_OVERVIEW_BRANDMANAGER_SECTION_BUTTON.MANAGE',
  },
};
const links = {
  [BrandmanagerStates.CREATE]: COMPANY_PROFILE_CREATION_URL,
  [BrandmanagerStates.FINALIZE]: COMPANY_PROFILE_CREATION_URL,
  [BrandmanagerStates.MANAGE]: TEAMPERMISSIONS_URL,
};
const trackingElements = {
  [BrandmanagerStates.CREATE]: 'create_company_button',
  [BrandmanagerStates.FINALIZE]: 'finish_company_creation_button',
  [BrandmanagerStates.MANAGE]: 'brand_manager_button',
};

export const SectionBrandmanager: FC = () => {
  const { $t } = useIntl();
  const trackNwtEvent = useNwtTrackingCompanies();

  const [brandmanagerState, setBrandmanagerState] =
    useState<BrandmanagerStates | null>(null);

  useQuery(SectionBrandmanagerDocument, {
    onCompleted: (data) => {
      const teampermissionsResources =
        data?.viewer?.teamPermissions?.userResources?.collection ?? [];

      const userEntityPages = data?.viewer?.userEntityPages?.collection ?? [];
      const companyInCreation = userEntityPages.find(
        (entityPage) =>
          entityPage?.focusType === EntityPageFocusType.Company &&
          entityPage?.publicationStatus === EntityPagePublicationStatus.Created
      );

      if (companyInCreation) {
        setBrandmanagerState(BrandmanagerStates.FINALIZE);
      } else if (teampermissionsResources?.length > 0) {
        setBrandmanagerState(BrandmanagerStates.MANAGE);
      } else {
        setBrandmanagerState(BrandmanagerStates.CREATE);
      }
    },
  });

  const handleOnClick = (
    ev: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    ev.stopPropagation();
    ev.preventDefault();
    if (brandmanagerState) {
      relocate(links[brandmanagerState]);

      trackNwtEvent({
        eventSchema: 'extended',
        page: 'companies/overview',
        event: 'opened',
        element: trackingElements[brandmanagerState],
        moduleTitle: 'brand_manager',
      });
    }
  };

  return (
    <Styled.CenterContainer data-testid="SECTION-BRANDMANAGER">
      <SectionGridContainer variant="FullSize">
        <Styled.GridItem span={12}>
          {brandmanagerState && (
            <Styled.LinkWrapper>
              <Styled.BodyCopy>
                {$t({ id: translationsStates[brandmanagerState].box })}
              </Styled.BodyCopy>

              <Styled.TextButton
                to={links[brandmanagerState]}
                icon={IconArrowRight}
                iconAfter={true}
                onClick={handleOnClick}
              >
                {$t({ id: translationsStates[brandmanagerState].button })}
              </Styled.TextButton>
            </Styled.LinkWrapper>
          )}
          <Styled.SmallDevice variant="info">
            {/** @ts-expect-error dangerous set inner html used */}
            <label
              dangerouslySetInnerHTML={{
                __html: $t({ id: 'CP_LANDING_MOBILE_BANNER' }),
              }}
            />
          </Styled.SmallDevice>
        </Styled.GridItem>
      </SectionGridContainer>
    </Styled.CenterContainer>
  );
};
